import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CallMergeIcon from "@mui/icons-material/CallMerge";

import useActions, { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  FunctionInfo,
  SliceBlock,
  closeFuncMap,
  startFuncMapSlice,
  clearFuncMapSlice,
  selectFuncMapOpen,
  selectFuncMapStatus,
  selectFuncMapFile,
  selectFuncMapRepoId,
  selectFuncMapStats,
  selectFuncMapMappedFiles,
  selectFuncMapFunctions,
  selectFuncMapCallees,
  selectFuncMapError,
  selectFuncMapSliceFor,
  selectFuncMapSliceName,
  selectFuncMapSliceStatus,
  selectFuncMapSliceBlocks,
  selectFuncMapSliceDirection,
} from "./librarySlice";
import { setFileDataChanged, setViewCodeState } from "../editor/dashEditorSlice";

const KIND_LABEL: { [k: string]: string } = {
  c_function: "C function",
  cpp_method: "C++ method",
  instance: "ObjC −",
  class: "ObjC +",
};

function qualifiedName(f: { class_name: string | null; name: string }): string {
  return f.class_name ? `${f.class_name}::${f.name}` : f.name;
}

function locationLabel(f: { file_path: string | null; line_number: number }): string {
  if (!f.file_path) return "unresolved";
  return f.line_number > 0 ? `${f.file_path}:${f.line_number}` : f.file_path;
}

export default function FunctionMapModal() {
  const dispatch = useAppDispatch();
  const actions = useActions();

  const open = useAppSelector(selectFuncMapOpen);
  const status = useAppSelector(selectFuncMapStatus);
  const file = useAppSelector(selectFuncMapFile);
  const repoId = useAppSelector(selectFuncMapRepoId);
  const stats = useAppSelector(selectFuncMapStats);
  const mappedFiles = useAppSelector(selectFuncMapMappedFiles);
  const functions = useAppSelector(selectFuncMapFunctions);
  const calleesMap = useAppSelector(selectFuncMapCallees);
  const error = useAppSelector(selectFuncMapError);

  const sliceFor = useAppSelector(selectFuncMapSliceFor);
  const sliceName = useAppSelector(selectFuncMapSliceName);
  const sliceStatus = useAppSelector(selectFuncMapSliceStatus);
  const sliceBlocks = useAppSelector(selectFuncMapSliceBlocks);
  const sliceDirection = useAppSelector(selectFuncMapSliceDirection);

  const [expanded, setExpanded] = React.useState<{ [id: number]: boolean }>({});
  const [query, setQuery] = React.useState("");

  // Clear the quick-search when a different file is mapped.
  React.useEffect(() => { setQuery(""); }, [file]);

  const handleClose = () => dispatch(closeFuncMap());

  const q = query.trim().toLowerCase();
  const filteredFunctions = q
    ? functions.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          (f.class_name ?? "").toLowerCase().includes(q) ||
          (f.signature ?? "").toLowerCase().includes(q)
      )
    : functions;

  const toggleExpand = (f: FunctionInfo) => {
    const isOpen = !!expanded[f.method_id];
    setExpanded((e) => ({ ...e, [f.method_id]: !isOpen }));
    if (!isOpen && calleesMap[f.method_id] === undefined) {
      actions.getFunctionCallees({ repoId, methodId: f.method_id });
    }
  };

  // Build a transitive slice for a function: "down" = what it calls,
  // "up" = what uses it (callers / reverse slice).
  const runSlice = (methodId: number, name: string, direction: "down" | "up") => {
    dispatch(startFuncMapSlice({ methodId, name, direction }));
    if (direction === "down") {
      actions.getFeatureSlice({ repoId, methodId });
    } else {
      actions.getReverseSlice({ repoId, methodId });
    }
  };

  const startSlice = (f: FunctionInfo, direction: "down" | "up") => {
    runSlice(f.method_id, qualifiedName(f), direction);
  };

  const openInEditor = (file_path: string | null) => {
    if (!file_path) return;
    dispatch(setFileDataChanged(false));
    dispatch(setViewCodeState(3));
    actions.openFile({ repoId, file_name: file_path });
    dispatch(closeFuncMap());
  };

  const renderFunctionList = () => (
    <>
      {stats && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {stats.files} files indexed
          {mappedFiles.length > 0 ? ` · ${mappedFiles.length} updated` : ""} · {stats.classes} classes ·{" "}
          {stats.methods} functions · {stats.dependencies} dependencies ({stats.resolved_pct}% resolved)
        </Typography>
      )}
      {functions.length > 0 && (
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Quick search functions…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      )}
      {functions.length === 0 ? (
        <Typography>No functions found in this file.</Typography>
      ) : filteredFunctions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No functions match “{query}”.</Typography>
      ) : (
        <List dense>
          {filteredFunctions.map((f) => {
            const isOpen = !!expanded[f.method_id];
            const callees = calleesMap[f.method_id];
            return (
              <Box key={f.method_id}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Direct dependencies">
                    <IconButton size="small" onClick={() => toggleExpand(f)} aria-label="expand dependencies">
                      {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Program slice — what this calls (down)">
                    <ListItemButton onClick={() => startSlice(f, "down")} sx={{ borderRadius: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {f.class_name && <Chip size="small" color="primary" variant="outlined" label={f.class_name} />}
                            <Chip size="small" variant="outlined" label={KIND_LABEL[f.method_kind] ?? f.method_kind} />
                            <strong>{f.name}</strong>
                            <Typography variant="caption" color="text.secondary">
                              {f.signature ? `(${f.signature}) · ` : ""}
                              {f.outbound} deps · {locationLabel(f)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </Tooltip>
                  <Tooltip title="Used by — what uses this (up)">
                    <IconButton size="small" onClick={() => startSlice(f, "up")} aria-label="used by">
                      <CallMergeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open source">
                    <span>
                      <IconButton size="small" disabled={!f.file_path} onClick={() => openInEditor(f.file_path)} aria-label="open source">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ pl: 6, pb: 1 }}>
                    {callees === undefined ? (
                      <Typography variant="caption" color="text.secondary">Loading…</Typography>
                    ) : callees.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No direct dependencies.</Typography>
                    ) : (
                      callees.map((c) => (
                        <Box
                          key={c.method_id}
                          onClick={() => openInEditor(c.file_path)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 0.25,
                            cursor: c.file_path ? "pointer" : "default",
                            "&:hover": { color: c.file_path ? "primary.main" : undefined },
                          }}
                        >
                          {c.class_name && <Chip size="small" variant="outlined" label={c.class_name} />}
                          <span>{c.name}</span>
                          <Typography variant="caption" color="text.secondary">{locationLabel(c)}</Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}
    </>
  );

  const renderSlice = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => dispatch(clearFuncMapSlice())}>
          Functions
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {sliceDirection === "up" ? "Used by" : "Program slice"} — {sliceName}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          startIcon={<CallSplitIcon />}
          variant={sliceDirection === "down" ? "contained" : "outlined"}
          onClick={() => sliceFor !== null && runSlice(sliceFor, sliceName, "down")}
        >
          Calls
        </Button>
        <Button
          size="small"
          startIcon={<CallMergeIcon />}
          variant={sliceDirection === "up" ? "contained" : "outlined"}
          onClick={() => sliceFor !== null && runSlice(sliceFor, sliceName, "up")}
        >
          Used by
        </Button>
      </Box>
      {sliceStatus === "loading" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <CircularProgress size={20} />
          <Typography>{sliceDirection === "up" ? "Finding callers…" : "Building program slice…"}</Typography>
        </Box>
      )}
      {sliceStatus === "failed" && <Typography color="error">Could not build the slice.</Typography>}
      {sliceStatus === "done" && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {sliceBlocks.length} block(s) ·{" "}
            {sliceDirection === "up" ? "callers, " : ""}sorted by distance (farthest first)
          </Typography>
          {sliceBlocks.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {sliceDirection === "up" ? "Nothing in the project calls this function." : "This function has no resolved dependencies."}
            </Typography>
          )}
          {sliceBlocks.map((b: SliceBlock, i: number) => (
            <Box key={`${b.method_id}_${i}`} sx={{ mb: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Chip size="small" label={`d${b.depth}`} color={b.depth === 0 ? "primary" : "default"} />
                {b.class_name && <Chip size="small" variant="outlined" label={b.class_name} />}
                <strong>{b.name}</strong>
                {b.is_cycle && <Chip size="small" color="warning" variant="outlined" label="cycle" />}
                {b.is_external && <Chip size="small" variant="outlined" label="external" />}
                <Typography variant="caption" color="text.secondary">{locationLabel(b)}</Typography>
                {b.file_path && (
                  <Tooltip title="Open source">
                    <IconButton size="small" onClick={() => openInEditor(b.file_path)} aria-label="open source">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {b.code ? (
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    fontSize: "0.78rem",
                    fontFamily: "monospace",
                    overflow: "auto",
                    maxHeight: 240,
                    whiteSpace: "pre",
                  }}
                >
                  {b.code}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  (no source available in this slice)
                </Typography>
              )}
            </Box>
          ))}
        </>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Function map — {file}</DialogTitle>
      <DialogContent dividers>
        {status === "mapping" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography>
              Mapping <strong>{file}</strong> — following includes…
            </Typography>
          </Box>
        )}
        {status === "failed" && <Typography color="error">Mapping failed: {error}</Typography>}
        {status === "done" && (sliceFor !== null ? renderSlice() : renderFunctionList())}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
